import React, { useState } from 'react'
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
  ContextMenuSeparator,
} from "@/components/ui/context-menu"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { useToast } from "@/hooks/use-toast"
import { Filter, Pin, Eye, EyeOff, Edit3, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react'

interface TableColumnContextMenuProps {
  children: React.ReactNode
  columnId: string
  columnLabel: string
  isPinned?: boolean
  isVisible?: boolean
  currentSort?: { column: string; direction: 'asc' | 'desc' } | null
  onPin?: (columnId: string) => void
  onUnpin?: (columnId: string) => void
  onHide?: (columnId: string) => void
  onShow?: (columnId: string) => void
  onRename?: (columnId: string, newLabel: string) => void
  onFilter?: (columnId: string) => void
  onSort?: (columnId: string, direction: 'asc' | 'desc') => void
}

export function TableColumnContextMenu({
  children,
  columnId,
  columnLabel,
  isPinned = false,
  isVisible = true,
  currentSort = null,
  onPin,
  onUnpin,
  onHide,
  onShow,
  onRename,
  onFilter,
  onSort,
}: TableColumnContextMenuProps) {
  const [showRenameDialog, setShowRenameDialog] = useState(false)
  const [newColumnName, setNewColumnName] = useState(columnLabel || '')
  const { toast } = useToast()

  // Update newColumnName when columnLabel changes
  React.useEffect(() => {
    setNewColumnName(columnLabel || '')
  }, [columnLabel])

  const handleRename = () => {
    const trimmedName = (newColumnName || '').trim()
    if (onRename && trimmedName && trimmedName !== columnLabel) {
      onRename(columnId, trimmedName)
      toast({
        title: "Column renamed",
        description: `Column "${columnLabel}" has been renamed to "${trimmedName}"`,
      })
    }
    setShowRenameDialog(false)
  }

  const handlePin = () => {
    if (isPinned && onUnpin) {
      onUnpin(columnId)
      toast({
        title: "Column unpinned",
        description: `Column "${columnLabel}" has been unpinned`,
      })
    } else if (!isPinned && onPin) {
      onPin(columnId)
      toast({
        title: "Column pinned",
        description: `Column "${columnLabel}" has been pinned`,
      })
    }
  }

  const handleVisibility = () => {
    if (isVisible && onHide) {
      onHide(columnId)
      toast({
        title: "Column hidden",
        description: `Column "${columnLabel}" has been hidden. You can show it again from the column manager.`,
      })
    } else if (!isVisible && onShow) {
      onShow(columnId)
      toast({
        title: "Column shown",
        description: `Column "${columnLabel}" is now visible`,
      })
    }
  }

  const handleFilter = () => {
    if (onFilter) {
      onFilter(columnId)
      toast({
        title: "Filter applied",
        description: `Filter has been applied to column "${columnLabel}"`,
      })
    }
  }

  const handleSort = (direction: 'asc' | 'desc') => {
    if (onSort) {
      onSort(columnId, direction)
      toast({
        title: "Column sorted",
        description: `Column "${columnLabel}" sorted in ${direction === 'asc' ? 'ascending' : 'descending'} order`,
      })
    }
  }

  const isCurrentlySorted = currentSort?.column === columnId
  const currentDirection = currentSort?.direction

  return (
    <>
      <ContextMenu>
        <ContextMenuTrigger asChild>
          {children}
        </ContextMenuTrigger>
        <ContextMenuContent className="w-48 bg-background border shadow-lg z-50">
          {onSort && (
            <>
              <ContextMenuItem 
                onClick={() => handleSort('asc')} 
                className={`gap-2 cursor-pointer hover:bg-accent focus:bg-accent ${
                  isCurrentlySorted && currentDirection === 'asc' ? 'bg-accent' : ''
                }`}
              >
                <ArrowUp className="h-4 w-4" />
                Sort Ascending
                {isCurrentlySorted && currentDirection === 'asc' && (
                  <span className="ml-auto text-xs">●</span>
                )}
              </ContextMenuItem>
              
              <ContextMenuItem 
                onClick={() => handleSort('desc')} 
                className={`gap-2 cursor-pointer hover:bg-accent focus:bg-accent ${
                  isCurrentlySorted && currentDirection === 'desc' ? 'bg-accent' : ''
                }`}
              >
                <ArrowDown className="h-4 w-4" />
                Sort Descending
                {isCurrentlySorted && currentDirection === 'desc' && (
                  <span className="ml-auto text-xs">●</span>
                )}
              </ContextMenuItem>
              <ContextMenuSeparator />
            </>
          )}
          
          {onFilter && (
            <>
              <ContextMenuItem 
                onClick={handleFilter} 
                className="gap-2 cursor-pointer hover:bg-accent focus:bg-accent"
              >
                <Filter className="h-4 w-4" />
                Filter on this column
              </ContextMenuItem>
              <ContextMenuSeparator />
            </>
          )}
          
          <ContextMenuItem 
            onClick={handlePin} 
            className="gap-2 cursor-pointer hover:bg-accent focus:bg-accent"
          >
            <Pin className={`h-4 w-4 ${isPinned ? 'fill-current text-blue-600' : ''}`} />
            {isPinned ? 'Unpin' : 'Pin'}
          </ContextMenuItem>
          
          <ContextMenuItem 
            onClick={handleVisibility} 
            className="gap-2 cursor-pointer hover:bg-accent focus:bg-accent"
          >
            {isVisible ? (
              <>
                <EyeOff className="h-4 w-4" />
                Hide
              </>
            ) : (
              <>
                <Eye className="h-4 w-4" />
                Show
              </>
            )}
          </ContextMenuItem>
          
          {onRename && (
            <ContextMenuItem 
              onClick={() => {
                setNewColumnName(columnLabel || '')
                setShowRenameDialog(true)
              }} 
              className="gap-2 cursor-pointer hover:bg-accent focus:bg-accent"
            >
              <Edit3 className="h-4 w-4" />
              Rename column
            </ContextMenuItem>
          )}
        </ContextMenuContent>
      </ContextMenu>

      <Dialog open={showRenameDialog} onOpenChange={setShowRenameDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Rename Column</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="column-name">Column Name</Label>
              <Input
                id="column-name"
                value={newColumnName || ''}
                onChange={(e) => setNewColumnName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleRename()
                  }
                }}
                autoFocus
              />
            </div>
          </div>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => {
                setShowRenameDialog(false)
                setNewColumnName(columnLabel || '')
              }}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleRename}
              disabled={!(newColumnName || '').trim() || (newColumnName || '').trim() === columnLabel}
            >
              Rename
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}